import { useMemo } from 'react';

import { useHdrProcessingRateMins } from '@/api/devices';
import {
    GetPartitionHistoryDataParams,
    GetPartitionHistoryDataResponse,
    PartitionHistoryItem,
    PartitionWaveDataItemFormattedWithUnits,
} from '@/api/historicalData';
import { combineProcessingSources } from '@/api/historicalData/helpers';
import { useLiveSplitQuery } from '@/api/useLiveSplitQuery';
import { RequestOptions, axios, requestConfig } from '@/common/axios';
import { convert } from '@/common/convert';
import { CombinedQueryResult, QueryConfig } from '@/common/react-query';
import { PARTITION_DATA_HISTORY_QUERY_KEY } from '@/config';
import { usePreferredUnits } from '@/hooks/usePreferredUnits';
import { DateRange, useGlobalStore } from '@/store';
import { maybeMockNestedHdrResponse } from '@/utils/mockDataHelpers';

export const getPartitionDataHistory = async (
    params: GetPartitionHistoryDataParams,
    options?: RequestOptions
): Promise<PartitionHistoryItem[]> => {
    const response: GetPartitionHistoryDataResponse = await maybeMockNestedHdrResponse(
        axios.get('/fetch/partition-data-history', {
            params,
            ...requestConfig(options),
        }),
        'partitionHistory'
    );

    return response.data.partitionHistory.map(({ location, ...item }) => ({ ...item, ...location }));
};

type UseHistoricalPartitionDataOptions = Omit<GetPartitionHistoryDataParams, 'spotterId' | 'startDate' | 'endDate'> & {
    spotterId?: string | null;
    dateRange?: DateRange | null;
    config?: Omit<QueryConfig<typeof getPartitionDataHistory>, 'select'>;
};

/**
 * Historical wave partitions for the range, in the user's preferred units, with embedded samples filling the
 * gaps between the hdr ones.
 */
export const useHistoricalPartitionData = ({
    spotterId,
    dateRange,
    processingSources,
    config,
}: UseHistoricalPartitionDataOptions): CombinedQueryResult<PartitionWaveDataItemFormattedWithUnits[]> => {
    const processingSource = useGlobalStore((state) => state.processingSource);
    const { data: partitionDataRaw, ...rest } = useLiveSplitQuery({
        queryKeyPrefix: PARTITION_DATA_HISTORY_QUERY_KEY,
        spotterId,
        dateRange,
        keyOptions: { processingSources },
        fetchRange: ({ viewToken, signal, ...range }) =>
            getPartitionDataHistory({ ...range, processingSources }, { viewToken, signal }),
        // one of the older Spotter entrypoints, so it excludes the bounds it is given
        apiDateBounds: 'exclusive',
        timestampKey: 'timestamp',
        sortOrder: 'asc',
        /*
         * wavefleet queries a window once per device row and concatenates the results. The sort it finishes
         * with keys on a top level `timestamp`, which these samples carry under `location`, so it does not
         * order them: a spotter whose device was replaced answers in device order, not time order.
         */
        responseSortOrder: 'unsorted',
        config,
    });
    const hdrProcessingRateMins = useHdrProcessingRateMins({
        spotterId,
        config: { enabled: config?.enabled !== false },
    });
    const units = usePreferredUnits('distance');
    const formatted = useMemo<PartitionWaveDataItemFormattedWithUnits[] | undefined>(
        () =>
            combineProcessingSources(
                partitionDataRaw?.map(({ partitions, timestamp, processing_source }) => {
                    const [swell, sea] = partitions;
                    const swellData = {
                        significantWaveHeightSwell: convert(swell.significantWaveHeight).from('m').to(units),
                        meanPeriodSwell: swell.meanPeriod,
                        meanDirectionSwell: swell.meanDirection,
                        meanDirectionalSpreadSwell: swell.meanDirectionalSpread,
                    };
                    const seaData = {
                        significantWaveHeightSea: convert(sea.significantWaveHeight).from('m').to(units),
                        meanPeriodSea: sea.meanPeriod,
                        meanDirectionSea: sea.meanDirection,
                        meanDirectionalSpreadSea: sea.meanDirectionalSpread,
                    };
                    return {
                        ...swellData,
                        ...seaData,
                        timestamp,
                        processing_source,
                        partitionSignificantWaveHeightUnits: units,
                        partitionSignificantWaveHeightUnitType: 'length',
                    };
                }),
                hdrProcessingRateMins,
                processingSource
            ),
        [partitionDataRaw, hdrProcessingRateMins, units, processingSource]
    );

    return { data: formatted, ...rest };
};
